// Import apiClient from proxy to avoid circular dependency
import apiClient from '../services/apiClientProxy';

// Helper to clean up URL
const cleanUrl = (url) => {
  if (!url) return '';
  return url.replace(/\/+$/, '');
};

// IMPORTANT: The gateway already adds the /api prefix when forwarding requests
// Do NOT include /api in the baseURL as it will cause double prefixing
const baseURL = cleanUrl(process.env.REACT_APP_API_URL) || 'http://localhost:9900';

// Check if V2 API is enabled (for optimized routes with caching)
const useV2API = process.env.REACT_APP_USE_V2_API === 'true';

// Helper to get versioned endpoint
const getVersionedEndpoint = (v1Path, v2Path) => {
  return useV2API ? v2Path : v1Path;
};

// Define endpoints without /api prefix since it's handled by the gateway
const endpoints = {
  auth: {
    login: '/auth/login',
    verify: '/auth/verify',
    refresh: '/auth/refresh',
    logout: '/auth/logout'
  },
  creators: {
    base: getVersionedEndpoint('/creators', '/v2/creators'),
    byId: (id) => getVersionedEndpoint(`/creators/${id}`, `/v2/creators/${id}`),
    byWallet: (wallet) => `/creators/wallet/${wallet}`,
    revenue: (wallet) => `/creators/wallet/${wallet}/revenue`,
    updateQrtPayment: '/creators/update-qrt-payment',
    updateShortFormPayment: '/creators/update-shortform-payment',
    // Workload endpoints
    workload: '/creators/workload',
    workloadBatch: '/creators/workload/batch',
    workloadById: (id) => `/creators/${id}/workload`,
    updateAvailability: (id) => `/creators/${id}/availability`,
    // Historical data endpoints (Week 8 MongoDB optimization)
    followersHistory: (id) => `/creators/${id}/followers-history`,
    messages: (id) => `/creators/${id}/messages`,
    transactions: (id) => `/creators/${id}/transactions`,
    campaigns: (id) => `/creators/${id}/campaigns`,
    analyticsSnapshots: (id) => `/creators/${id}/analytics-snapshots`
  },
  campaigns: {
    base: '/campaigns',
    byId: (id) => `/campaigns/${id}`,
    delete: (id) => `/campaigns/${id}`,
    status: (id) => `/campaigns/${id}/status`,
    creators: (id) => `/campaigns/${id}/creators`,
    removedCreators: (id) => `/campaigns/${id}/creators/removed`,
    removeCreator: (campaignId, creatorId) => `/campaigns/${campaignId}/creators/${creatorId}`,
    restoreCreator: (campaignId, creatorId) => `/campaigns/${campaignId}/creators/${creatorId}/restore`,
    deleteCreator: (campaignId, creatorId) => `/campaigns/${campaignId}/creators/${creatorId}/delete`,
    creatorStatus: (campaignId, creatorId) => `/campaigns/${campaignId}/creators/${creatorId}/status`,
    // Special endpoint for private campaigns
    privateCreatorStatus: (campaignId, creatorId) => `/campaigns/${campaignId}/creators/${creatorId}/private-status`,
    // New endpoints for batch operations
    updateCreatorsStatus: (campaignId) => `/campaigns/${campaignId}/creators/status`,
    assignRoles: (campaignId) => `/campaigns/${campaignId}/creators/assign-roles`,
    assignRole: (campaignId) => `/campaigns/${campaignId}/assign-role`,
    assignRolesBulk: (campaignId) => `/campaigns/${campaignId}/assign-roles-bulk`,
    directAssignRoles: (campaignId) => `/campaigns/${campaignId}/direct-assign-roles`,
    pricingType: (campaignId) => `/campaigns/${campaignId}/pricing-type`,
    publicReport: (id) => `/campaigns/${id}/public-report`
  },
  payments: {
    history: '/payments/history',
    base: '/payments',
    create: '/payments/create',
    update: (id) => `/payments/${id}`,
    delete: (id) => `/payments/${id}`
  },
  brands: {
    base: '/brands',
    byId: (id) => `/brands/${id}`
  },
  twitter: {
    user: (username) => `/twitter/user/${username}`,
    userByUsername: (username) => `/twitter/user/username/${username}`,
    followers: (id) => `/twitter/followers/${id}`,
    followersByUserId: (id) => `/twitter/followers/user/${id}`,
    history: (creatorId) => `/twitter/history/${creatorId}`,
    metrics: (creatorId) => `/twitter/metrics/${creatorId}`,
    dashboardStats: '/twitter/dashboard-stats',
    chartData: '/twitter/chart-data',
    syncFollowers: '/twitter/sync/followers',
    syncFollowersStatus: '/twitter/sync/followers/status',
    syncCampaign: (campaignId) => `/twitter-sync/${campaignId}/sync`,
    stats: '/twitter/stats'
  },
  admin: {
    twitterServiceConfig: '/admin/twitter-service/config',
    testTwitterServiceConnection: '/admin/twitter-service/test-connection',
    twitterServiceStats: '/admin/twitter-service/stats'
  },
  discord: {
    channels: (guildId) => `/discord/channels/${guildId}`,
    categories: (guildId) => `/discord/categories/${guildId}`,
    settings: '/discord/settings',
    emojis: '/discord/emojis',
    status: '/discord/status',
    invite: '/discord/invite',
    messages: {
      send: (campaignId) => `/discord/campaigns/${campaignId}/message`,
      update: (campaignId) => `/discord/campaigns/${campaignId}/message`,
      reactions: (campaignId) => `/discord/campaigns/${campaignId}/reactions`,
      details: (messageId) => `/discord/messages/${messageId}`,
      emojis: (messageId) => `/discord/messages/${messageId}/emojis`,
      sendThread: '/discord/send-thread-message'
    },
    campaigns: {
      briefing: (campaignId) => `/discord/campaigns/${campaignId}/briefing`,
      syncMessage: (campaignId) => `/discord/campaigns/${campaignId}/sync-message`
    }
  },
  deals: {
    base: '/deals',
    byId: (id) => `/deals/${id}`,
    create: '/deals/create'
  },
  revenue: {
    topEarners: '/revenue/top-earners',
    stats: '/revenue/stats',
    historical: '/revenue/historical'
  },
  messages: {
    threads: {
      list: '/messages/threads',
      get: (threadId) => `/messages/threads/${threadId}`,
      create: '/messages/threads/create',
      archive: (threadId) => `/messages/threads/${threadId}/archive`,
      createForRole: '/messages/create-threads-for-role',
      markRead: (threadId) => `/messages/threads/${threadId}/read`,
      unreadCounts: () => '/messages/threads/unread-counts'
    },
    send: '/messages/send',
    search: '/messages/search',
    creator: (creatorId) => `/messages/creator/${creatorId}`
  },
  settings: '/settings',
  nft: {
    sync: '/nft/sync',
    status: '/nft/status'
  },
  reporting: {
    campaigns: '/reporting/campaigns',
    campaignDetail: (id) => `/reporting/campaigns/${id}`,
    updateStage: (id) => `/reporting/campaigns/${id}/stage`
  }
};

// Export the API configuration
const API_CONFIG = {
  baseURL,
  endpoints
};

export { endpoints, apiClient };
export default API_CONFIG;